import { Button, ListenButton } from '@/components/atoms'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import { studentWordbookApi, API_BASE_URL } from '@/services/api'
import { getSlugPrefix } from '@/utils/slug'
import { formatNumber } from '@/utils/number'

type WordItem = { id?: number; word: string; meaning: string; audioUrl?: string | null };
type AnswerSnapshot = { input: string; checkedCorrect: boolean; attempts: number };
const EMPTY_SNAPSHOT: AnswerSnapshot = { input: '', checkedCorrect: false, attempts: 0 };

const getSnapshotKey = (word?: WordItem, fallbackIndex?: number) => {
  if (word?.id !== undefined && word?.id !== null) {
    return `word-${word.id}`;
  }
  if (typeof fallbackIndex === 'number') {
    return `index-${fallbackIndex}`;
  }
  return '';
};


export default function StudentVocabularyBookDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const slugPrefix = getSlugPrefix(location.pathname);
  const [searchParams] = useSearchParams();
  const wordbookId = Number(searchParams.get('wordbookId') || 0);

  const [title, setTitle] = useState('단어장');
  const [words, setWords] = useState<WordItem[]>([]);
  const [index, setIndex] = useState(0);
  const [input, setInput] = useState('');
  const [checkedCorrect, setCheckedCorrect] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [solvedIds, setSolvedIds] = useState<Set<number>>(new Set());
  const [isProcessingAnswer, setIsProcessingAnswer] = useState(false);
  const answersRef = useRef<Record<string, AnswerSnapshot>>({});
  const hasProgressRef = useRef(false);
  const sessionSyncedRef = useRef(false);
  const metricsRef = useRef<{ maxStreak: number; totalItems: number }>({ maxStreak: 0, totalItems: 0 });
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const total = words.length;
  const current = words[index];
  const currentSnapshotKey = useMemo(() => getSnapshotKey(current, index), [current?.id, index]);
  const isLastWord = total > 0 && index === total - 1;
  const canGoPrev = index > 0;
  const canGoNext = index < total - 1;
  const hasCompleteButton = isLastWord && !canGoNext;
  const navigationButtonsCount = (canGoPrev ? 1 : 0) + ((canGoNext || hasCompleteButton) ? 1 : 0);
  const isSingleNavigationVisible = navigationButtonsCount === 1;

  const normalize = (s: string) => (s || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const isMatch = useMemo(() => normalize(input) === normalize(current?.word || ''), [input, current]);

  const clearAutoAdvanceTimer = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!wordbookId) return;
      try {
        // @ts-ignore
        const res = await studentWordbookApi.detail(wordbookId);
        const data = res?.data || res;
        const nextWords = Array.isArray(data?.words) ? data.words : [];
        setTitle(data?.title || '단어장');
        setWords(nextWords);
        setIndex(0);
        answersRef.current = {};
        setCurrentStreak(0);
        setMaxStreak(0);
        setSolvedIds(new Set());
        hasProgressRef.current = false;
        sessionSyncedRef.current = false;
        metricsRef.current = { maxStreak: 0, totalItems: nextWords.length };
      } catch (e) {
        console.error('단어장 상세 조회 실패:', e);
      }
    };
    fetchDetail();

    return () => {
      clearAutoAdvanceTimer();
    };
  }, [wordbookId, clearAutoAdvanceTimer]);

  useEffect(() => {
    if (!current || !currentSnapshotKey) {
      setInput('');
      setCheckedCorrect(false);
      setAttempts(0);
      return;
    }
    const snapshot = answersRef.current[currentSnapshotKey];
    if (snapshot) {
      setInput(snapshot.input);
      setCheckedCorrect(snapshot.checkedCorrect);
      setAttempts(snapshot.attempts);
    } else {
      setInput('');
      setCheckedCorrect(false);
      setAttempts(0);
    }
  }, [current, currentSnapshotKey]);

  const updateAnswerSnapshot = (updates: Partial<AnswerSnapshot>) => {
    if (!currentSnapshotKey) return;
    const prev = answersRef.current[currentSnapshotKey] || EMPTY_SNAPSHOT;
    answersRef.current[currentSnapshotKey] = { ...prev, ...updates };
  };

  const completeLastWord = async () => {
    if (!isLastWord || isProcessingAnswer) return;
    clearAutoAdvanceTimer();
    markProgress();

    const firstTry = attempts === 0;
    const currId = current?.id;
    const alreadySolved = currId ? solvedIds.has(currId) : false;

    let nextCurrentStreak = currentStreak;
    let nextMaxStreak = maxStreak;

    setIsProcessingAnswer(true);
    try {
      if (firstTry && !alreadySolved) {
        try {
          await studentWordbookApi.checkWord(wordbookId, { isCorrect: !!isMatch });
        } catch (error) {
          console.error('단어 체크 API 호출 실패:', error);
        }

        if (isMatch) {
          nextCurrentStreak = currentStreak + 1;
          nextMaxStreak = Math.max(maxStreak, nextCurrentStreak);
        } else {
          nextCurrentStreak = 0;
        }
      } else if (!isMatch) {
        nextCurrentStreak = 0;
      }

      try {
        // @ts-ignore
        await studentWordbookApi.upsertProgress(wordbookId, { maxStreak: nextMaxStreak });
      } catch (e) {
        console.error('진행 업서트 실패:', e);
      }

      setCurrentStreak(nextCurrentStreak);
      setMaxStreak(nextMaxStreak);
      if (currId && isMatch && !alreadySolved) {
        setSolvedIds(prev => {
          const ns = new Set(prev);
          ns.add(currId);
          return ns;
        });
      }

      await endSession(nextMaxStreak, true);
    } finally {
      setIsProcessingAnswer(false);
    }
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    setCheckedCorrect(false);
    updateAnswerSnapshot({ input: value, checkedCorrect: false });
  };

  const markProgress = () => {
    if (!hasProgressRef.current) {
      hasProgressRef.current = true;
    }
  };

  useEffect(() => {
    metricsRef.current = { maxStreak, totalItems: total };
  }, [maxStreak, total]);

  const syncSession = useCallback(async ({ useBeacon = false, force = false, overrideMaxStreak, completed = false }: { useBeacon?: boolean; force?: boolean; overrideMaxStreak?: number; completed?: boolean } = {}) => {
    if (!wordbookId) return;
    if (sessionSyncedRef.current && !force) return;
    if (!hasProgressRef.current && !force) return;

    const totalItems = metricsRef.current.totalItems || total;
    if (!totalItems) return;
    const payload = {
      maxStreak: typeof overrideMaxStreak === 'number' ? overrideMaxStreak : metricsRef.current.maxStreak || 0,
      totalItems,
      completed,
    };

    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const url = `${API_BASE_URL}/student/wordbooks/${wordbookId}/sessions`;
      const body = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(url, body);
      sessionSyncedRef.current = true;
      return;
    }

    try {
      // @ts-ignore
      await studentWordbookApi.saveSession(wordbookId, payload);
      sessionSyncedRef.current = true;
    } catch (e) {
      console.error('세션 자동 저장 실패:', e);
    }
  }, [total, wordbookId]);

  useEffect(() => {
    return () => {
      syncSession();
    };
  }, [syncSession]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleBeforeUnload = () => {
      syncSession({ useBeacon: true });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        syncSession({ useBeacon: true });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [syncSession]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = async (e) => {
    if (e.key === 'Enter') {
      markProgress();
      clearAutoAdvanceTimer();
      const firstTry = attempts === 0;
      const currId = current?.id;
      const alreadySolved = currId ? solvedIds.has(currId) : false;
      
      let nextCurrentStreak = currentStreak;
      let nextMaxStreak = maxStreak;

      // 정오답 피드백 먼저 표시
      setCheckedCorrect(isMatch);
      updateAnswerSnapshot({ checkedCorrect: isMatch });
      setAttempts((n) => {
        const next = n + 1;
        updateAnswerSnapshot({ attempts: next });
        return next;
      });

      // 세션 streak 업데이트
      try {
        if (!alreadySolved && firstTry) {
          await studentWordbookApi.checkWord(wordbookId, { isCorrect: !!isMatch });
          if (isMatch) {
            nextCurrentStreak = currentStreak + 1;
            nextMaxStreak = Math.max(maxStreak, nextCurrentStreak);
          } else {
            nextCurrentStreak = 0;
          }
        } else if (!isMatch) {
          nextCurrentStreak = 0;
        }
      } catch (error) {
        console.error('단어 체크 API 호출 실패:', error);
      }
          
      // 0.5초 피드백 후 다음으로 이동 (마지막이면 세션 종료)
      autoAdvanceTimerRef.current = setTimeout(async () => {
        autoAdvanceTimerRef.current = null;
        try {
          // 진행 업서트: 최대 연속 정답 갱신 반영
          // @ts-ignore
          await studentWordbookApi.upsertProgress(wordbookId, { maxStreak: nextMaxStreak });
        } catch (e) {
          console.error('진행 업서트 실패:', e);
        }

        // 상태 반영
        setCurrentStreak(nextCurrentStreak);
        setMaxStreak(nextMaxStreak);
        if (currId && isMatch && !alreadySolved) {
          setSolvedIds(prev => {
            const ns = new Set(prev);
            ns.add(currId);
            return ns;
          });
        }

        if (index < total - 1) {
          setIndex(index + 1);
        } else {
          // 마지막 문제였으면 세션 저장 후 종료
          await endSession(undefined, true);
        }
      }, 500);
    }
  };

  const goPrev = () => {
    if (index <= 0) return;
    clearAutoAdvanceTimer();
    const prev = index - 1;
    setIndex(prev);
  };

  const goNext = async () => {
    if (index >= total - 1 || isProcessingAnswer) return;
    clearAutoAdvanceTimer();
    markProgress();
    const firstTry = attempts === 0;
    const currId = current?.id;
    const alreadySolved = currId ? solvedIds.has(currId) : false;

    let nextCurrentStreak = currentStreak;
    let nextMaxStreak = maxStreak;

    setIsProcessingAnswer(true);
    try {
      if (firstTry && !alreadySolved) {
        try {
          await studentWordbookApi.checkWord(wordbookId, { isCorrect: !!isMatch });
        } catch (error) {
          console.error('단어 체크 API 호출 실패:', error);
        }

        if (isMatch) {
          nextCurrentStreak = currentStreak + 1;
          nextMaxStreak = Math.max(maxStreak, nextCurrentStreak);
        } else {
          nextCurrentStreak = 0;
        }
      } else if (!isMatch) {
        nextCurrentStreak = 0;
      }

      try {
        // @ts-ignore
        await studentWordbookApi.upsertProgress(wordbookId, { maxStreak: nextMaxStreak });
      } catch (e) {
        console.error('진행 업서트 실패:', e);
      }

      setCurrentStreak(nextCurrentStreak);
      setMaxStreak(nextMaxStreak);
      if (currId && isMatch && !alreadySolved) {
        setSolvedIds(prev => {
          const ns = new Set(prev);
          ns.add(currId);
          return ns;
        });
      }

      setIndex(index + 1);
    } finally {
      setIsProcessingAnswer(false);
    }
  };

  const endSession = async (overrideMaxStreak?: number, completed = false) => {
    clearAutoAdvanceTimer();
    try {
      hasProgressRef.current = true;
      await syncSession({ force: true, overrideMaxStreak: overrideMaxStreak ?? maxStreak, completed });
    } catch (e) {
      console.error('세션 저장 실패:', e);
    } finally {
      navigate(`${slugPrefix}/vocabulary/book?wordbookId=${wordbookId}`);
    }
  };

  return (
    <main className='flex flex-1 w-full mt-10 bg-transparent shadow-md !rounded-t-2xl md:my-10 md:justify-center card '>
      <div className='flex flex-col items-center w-full'>
        <div className="flex justify-between items-center px-5 pt-5 pb-3 w-full text-lg font-bold border-b border-gray-100">
          <h1 className=''>{title}</h1>
          <span className=''>({formatNumber(total ? index + 1 : 0)}/{formatNumber(total)})</span>
        </div>

        <div className='flex flex-col flex-1 gap-4 justify-between max-w-[600px] w-full items-center *:w-full' >
          <section className='flex flex-col gap-4 p-5'>
            <div className='flex relative justify-center items-center p-5 py-8 rounded-lg border border-gray-200'>
              <p className='text-2xl font-bold'>{current?.meaning || ''}</p>
              <ListenButton className='absolute top-2 right-2' size={40} audioSrc={current?.audioUrl || undefined} />
            </div>
            <input
              className={`${styles.graInput} placeholder:text-gray-500`}
              placeholder='해석에 맞는 영단어를 입력해주세요.'
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {attempts > 0 && !checkedCorrect && (
              <p className='text-sm text-red-500'>정답이 아닙니다. 다시 입력해 주세요.</p>
            )}
            {checkedCorrect && (
              <p className='text-sm text-green-600'>정답입니다!</p>
            )}
          </section>

          <section className='flex flex-col gap-4 p-5'>
            <div className={`flex gap-4 w-full ${isSingleNavigationVisible ? 'justify-center' : '*:w-full'}`}>
              {canGoPrev && (
                <Button intent='secondary' size='large' onClick={goPrev} disabled={isProcessingAnswer}>
                  이전
                </Button>
              )}
              {canGoNext ? (
                <Button intent='primary' size='large' onClick={goNext} disabled={isProcessingAnswer}>
                  다음
                </Button>
              ) : hasCompleteButton ? (
                <Button intent='primary' size='large' onClick={completeLastWord} disabled={isProcessingAnswer}>
                  학습 완료
                </Button>
              ) : null}
            </div>
            <div className='flex justify-between items-center'>
          <span className='text-sm text-gray-500'>연속 정답: {formatNumber(currentStreak)}</span>
              <Button intent='secondary' size='small' text className='underline' onClick={() => endSession()} disabled={isProcessingAnswer}>단어 학습 종료하기</Button>
            </div>
          </section>
        </div>
      </div>
    </main>
  )
}